import React, {
    useState,
    useEffect,
    useRef
} from 'react';
import range from 'lodash/range';
import map from 'lodash/map';
import isUndefined from 'lodash/isUndefined';
import { usePrevious } from '@codexporer.io/react-hooks';
import {
    timeSpanToSeconds,
    secondsToTimeSpan
} from '@race-planner/unit-converters';
import { Dropdown } from '@fluentui/react/lib/Dropdown';
import { Label } from '@fluentui/react/lib/Label';
import {
    Root,
    Row
} from './styled';

const selectOptionsMap = {
    days: map(range(0, 100), days => ({
        text: `${days}d`,
        key: `${days}`,
        value: days
    })),
    hours: map(range(0, 24), hours => ({
        text: `${hours}h`,
        key: `${hours}`,
        value: hours
    })),
    minutes: map(range(0, 60), minutes => ({
        text: `${minutes}min`,
        key: `${minutes}`,
        value: minutes
    })),
    seconds: map(range(0, 60), seconds => ({
        text: `${seconds}s`,
        key: `${seconds}`,
        value: seconds
    }))
};

export const TimeSpanPicker = ({
    value,
    onValueChange,
    label,
    disabled,
    shouldDisplayDays = true,
    shouldDisplayHours = true
}) => {
    const {
        days,
        hours,
        minutes,
        seconds
    } = secondsToTimeSpan(value);
    const onValueChangeRef = useRef();
    onValueChangeRef.current = onValueChange;
    const [selectedDays, setSelectedDays] = useState(days);
    const [selectedHours, setSelectedHours] = useState(hours);
    const [selectedMinutes, setSelectedMinutes] = useState(minutes);
    const [selectedSeconds, setSelectedSeconds] = useState(seconds);

    const previousSelectedDays = usePrevious(selectedDays);
    const previousSelectedHours = usePrevious(selectedHours);
    const previousSelectedMinutes = usePrevious(selectedMinutes);
    const previousSelectedSeconds = usePrevious(selectedSeconds);
    useEffect(
        () => {
            !isUndefined(previousSelectedDays) &&
                !isUndefined(previousSelectedHours) &&
                !isUndefined(previousSelectedMinutes) &&
                !isUndefined(previousSelectedSeconds) &&
                onValueChangeRef.current?.(timeSpanToSeconds({
                    days: selectedDays,
                    hours: selectedHours,
                    minutes: selectedMinutes,
                    seconds: selectedSeconds
                }));
        },
        [
            previousSelectedDays,
            previousSelectedHours,
            previousSelectedMinutes,
            previousSelectedSeconds,
            selectedDays,
            selectedHours,
            selectedMinutes,
            selectedSeconds
        ]
    );

    const previousValue = usePrevious(value);
    useEffect(
        () => {
            if (isUndefined(previousSelectedDays) ||
                previousSelectedDays !== selectedDays ||
                isUndefined(previousSelectedHours) ||
                previousSelectedHours !== selectedHours ||
                isUndefined(previousSelectedMinutes) ||
                previousSelectedMinutes !== selectedMinutes ||
                isUndefined(previousSelectedSeconds) ||
                previousSelectedSeconds !== selectedSeconds ||
                isUndefined(previousValue) ||
                previousValue === value) {
                return;
            }

            const {
                days,
                hours,
                minutes,
                seconds
            } = secondsToTimeSpan(value);

            setSelectedDays(days);
            setSelectedHours(hours);
            setSelectedMinutes(minutes);
            setSelectedSeconds(seconds);
        },
        [
            previousSelectedDays,
            previousSelectedHours,
            previousSelectedMinutes,
            previousSelectedSeconds,
            selectedDays,
            selectedHours,
            selectedMinutes,
            selectedSeconds,
            previousValue,
            value
        ]
    );

    const createChangeHandler = setValue => (event, option) => {
        setValue(option.value);
    };

    return (
        <Root disabled={disabled}>
            {label && (
                <Label>{label}</Label>
            )}
            <Row
                childCount={2 + (shouldDisplayDays ? 1 : 0) + (shouldDisplayHours ? 1 : 0)}
            >
                {shouldDisplayDays && (
                    <Dropdown
                        selectedKey={`${selectedDays}`}
                        options={selectOptionsMap.days}
                        onChange={createChangeHandler(setSelectedDays)}
                        disabled={disabled}
                    />
                )}
                {shouldDisplayHours && (
                    <Dropdown
                        selectedKey={`${selectedHours}`}
                        options={selectOptionsMap.hours}
                        onChange={createChangeHandler(setSelectedHours)}
                        disabled={disabled}
                    />
                )}
                <Dropdown
                    selectedKey={`${selectedMinutes}`}
                    options={selectOptionsMap.minutes}
                    onChange={createChangeHandler(setSelectedMinutes)}
                    disabled={disabled}
                />
                <Dropdown
                    selectedKey={`${selectedSeconds}`}
                    options={selectOptionsMap.seconds}
                    onChange={createChangeHandler(setSelectedSeconds)}
                    disabled={disabled}
                />
            </Row>
        </Root>
    );
};
